// src/components/Control/robot/events.ts
// Система событий страницы робота: единая история, всплывающие уведомления,
// звук и уведомления браузера. Подписки живут, пока открыта страница робота,
// на любом экране (карта, камера, няня, настройки):
//   audio          -> речь робота на сайте {"text", "wav_b64"} (режим звука «на сайте»)
//   audio/event    -> события няни (loud, noise, unusual, keyword) и голоса
//                     (voice_command, voice_text, error)
//   vision/events  -> что видит робот {kind, text, alert}
//   bt/state       -> ошибки Bluetooth
//   trainer/event  -> фитнес-тренер: повторы, итоги подходов, подсказки; с голосом «на сайте»
//                     фраза "say" звучит в браузере
//   home/event     -> умный дом дворецкого: что сделала лента (ошибки — всплывающим)
// Плюс события, выведенные из телеметрии: человек появился / пропал из кадра,
// смена режима автономии, итог поездки к точке.
import { useEffect, useRef } from 'react';
import * as zenoh from '@eclipse-zenoh/zenoh-ts';
import { Store } from './store';
import { HomeEvent, TelemetryStore, TrainerEvent, MODE_NAMES, STALE, fresh } from './telemetry';
import { useZenohJson } from '../../../hooks/useZenohSubscription';
import { AudioEvent, BtState, NANNY_EVENT_TYPES, VisionEvent, loadPref } from '../../../types/features';
import { NotificationItem } from '../../../types';
import { beep, speak } from '../../../audio/pcm';

export type EventSource = 'nanny' | 'vision' | 'voice' | 'person' | 'robot' | 'mission' | 'bt' | 'trainer' | 'home' | 'system';
export type EventLevel = 'info' | 'ok' | 'warn' | 'alert' | 'error';

export interface RobotEvent {
  id: number;
  at: number;
  source: EventSource;
  level: EventLevel;
  text: string;
  kind?: string;      // тип события ноды (loud, keyword, voice_command, fall...)
  clip?: string | null;
  levelDb?: number;
  heard?: string;
}

export const SOURCE_NAMES: Record<EventSource, string> = {
  nanny: 'Няня', vision: 'Зрение', voice: 'Голос', person: 'Люди', robot: 'Речь робота',
  mission: 'Автономия', bt: 'Bluetooth', trainer: 'Тренер', home: 'Умный дом', system: 'Система',
};

export interface EventsState {
  events: RobotEvent[];     // новые первыми
  toasts: RobotEvent[];
  // Когда человек был в кадре камеры 1: любой и по именам (узнанные по одежде)
  presence: { anyAt: number; byName: Record<string, number> };
  ackAt: number;            // оператор подтвердил тревогу
}

export type EventsStore = Store<EventsState>;

export const initialEvents: EventsState = { events: [], toasts: [], presence: { anyAt: 0, byName: {} }, ackAt: 0 };

const MAX_EVENTS = 300;
const TOAST_MS: Record<EventLevel, number> = { info: 3500, ok: 3500, warn: 6000, alert: 10000, error: 8000 };
// Человек пропал из кадра дольше чем на столько — событие «потерял человека»
export const PERSON_LOST_MS = 5000;

// Какие источники показывают всплывающие уведомления (настройка «для этого браузера»)
export const TOAST_PREF = 'events.toasts';
export function toastSources(): Record<EventSource, boolean> {
  return {
    nanny: true, vision: true, voice: true, person: false, robot: true, mission: true, bt: true, trainer: true, home: true,
    system: true,
    ...loadPref<Partial<Record<EventSource, boolean>>>(TOAST_PREF, {}),
  };
}

let seq = 0;

export function pushEvent(store: EventsStore, e: Omit<RobotEvent, 'id' | 'at'> & { at?: number }, toast = false) {
  const ev: RobotEvent = { id: ++seq, at: e.at ?? Date.now(), ...e };
  const showToast = toast && toastSources()[ev.source] !== false;
  store.set(s => ({
    events: [ev, ...s.events].slice(0, MAX_EVENTS),
    toasts: showToast ? [...s.toasts.slice(-3), ev] : s.toasts,
  }));
  if (showToast) {
    setTimeout(() => store.set(s => ({ toasts: s.toasts.filter(t => t.id !== ev.id) })), TOAST_MS[ev.level]);
  }
  return ev;
}

export function dismissToast(store: EventsStore, id: number) {
  store.set(s => ({ toasts: s.toasts.filter(t => t.id !== id) }));
}

// Совместимость с onNotify(message, type) панелей
export function notifier(store: EventsStore) {
  return (message: string, type: NotificationItem['type'] = 'info') => {
    const level: EventLevel = type === 'error' ? 'error' : type === 'warning' ? 'warn' : type === 'success' ? 'ok' : 'info';
    pushEvent(store, { source: 'system', level, text: message }, level !== 'info');
  };
}

function playWav(b64: string) {
  try {
    const bytes = Uint8Array.from(atob(b64), c => c.charCodeAt(0));
    const url = URL.createObjectURL(new Blob([bytes], { type: 'audio/wav' }));
    const audio = new Audio(url);
    audio.onended = () => URL.revokeObjectURL(url);
    audio.play().catch(err => console.warn('audio blocked by the browser (click the page first)', err));
  } catch (err) {
    console.warn('bad robot speech message', err);
  }
}

function browserNotify(title: string, body: string, tag: string) {
  if (!('Notification' in window) || Notification.permission !== 'granted') return;
  try {
    new Notification(title, { body, tag });
  } catch {
    /* мобильный Chrome требует service worker — тогда только список */
  }
}

export function useEventSystem(events: EventsStore, telemetry: TelemetryStore, session: zenoh.Session | null, robotId: string) {
  const p = `robot/${robotId}`;

  // Речь робота на сайте
  useZenohJson<{ text: string; wav_b64: string }>(session, `${p}/audio`, msg => {
    if (msg.wav_b64) playWav(msg.wav_b64);
    if (msg.text) pushEvent(events, { source: 'robot', level: 'info', text: `Робот сказал: «${msg.text}»` });
  });

  // Няня и голос
  useZenohJson<AudioEvent>(session, `${p}/audio/event`, ev => {
    if (NANNY_EVENT_TYPES.includes(ev.type)) {
      const strong = ev.type === 'loud' || ev.type === 'keyword';
      pushEvent(events, {
        source: 'nanny', level: strong ? 'alert' : 'warn', kind: ev.type, text: ev.text, clip: ev.clip, levelDb: ev.level_db,
      }, true);
      if (loadPref('nanny.beep', true)) beep(strong ? 1200 : 880);
      if (loadPref('nanny.notify', false)) browserNotify('FleetCore — режим няни', `Робот ${robotId}: ${ev.text}`, `nanny-${robotId}`);
    } else if (ev.type === 'voice_command' || ev.type === 'voice_text' || ev.type === 'error') {
      const butler = ev.source === 'butler';
      pushEvent(events, {
        source: 'voice', level: ev.type === 'error' ? 'error' : ev.type === 'voice_command' ? 'ok' : 'info',
        kind: ev.type, text: ev.type === 'error' ? `Звук: ${ev.text}` : butler ? `Грач: ${ev.text}` : ev.text, heard: ev.heard,
      }, ev.type === 'error');
    } else if (ev.type === 'butler') {
      pushEvent(events, { source: 'voice', level: 'info', kind: ev.type, text: 'Грач слушает…', heard: ev.heard });
    }
  });

  // Зрение
  useZenohJson<VisionEvent>(session, `${p}/vision/events`, ev => {
    const level: EventLevel = ev.kind === 'error' ? 'error' : ev.alert ? 'alert' : ev.kind === 'describe' ? 'ok' : 'info';
    pushEvent(events, { source: 'vision', level, kind: ev.kind, text: ev.kind === 'error' ? `Зрение: ${ev.text}` : ev.text },
      level === 'error' || level === 'alert' || ev.kind === 'describe');
    if (ev.alert) {
      if (loadPref('vision.beep', true)) beep(1200, 400);
      if (loadPref('nanny.notify', false)) browserNotify('FleetCore — зрение', `Робот ${robotId}: ${ev.text}`, `vision-${robotId}`);
    }
    if (ev.kind !== 'error' && loadPref('vision.speakBrowser', false)) speak(ev.text);
  });

  // Фитнес-тренер: повторы — только в историю, итоги и подсказки — ещё и всплывающим
  useZenohJson<TrainerEvent>(session, `${p}/trainer/event`, ev => {
    if (!ev.text) return;
    pushEvent(events, { source: 'trainer', level: ev.level === 'warn' ? 'warn' : ev.level === 'ok' ? 'ok' : 'info', kind: ev.kind, text: ev.text },
      ['set', 'done', 'stop', 'hint', 'ready'].includes(ev.kind));
    const voice = telemetry.get().trainer?.config.voice;
    if (ev.say && (voice === 'site' || voice === 'both')) speak(ev.say);
  });

  // Умный дом: каждая лента отвечает за себя; ошибки — всплывающим
  useZenohJson<HomeEvent>(session, `${p}/home/event`, ev => {
    if (!ev.text) return;
    pushEvent(events, { source: 'home', level: ev.ok ? 'ok' : 'error', kind: ev.type, text: ev.text }, !ev.ok);
  });

  // Bluetooth: новая ошибка
  const btError = useRef<string | null>(null);
  useZenohJson<BtState>(session, `${p}/bt/state`, msg => {
    if (msg.error && msg.error !== btError.current) {
      pushEvent(events, { source: 'bt', level: 'error', text: `Bluetooth: ${msg.error}` }, true);
    }
    btError.current = msg.error;
  });

  // События из телеметрии: люди в кадре, режим автономии, итог поездки
  useEffect(() => {
    let present = false;
    let lastNames = '';
    let mode: string | null = null;
    let result: string | undefined;
    const check = () => {
      const t = telemetry.get();
      const now = Date.now();
      const inFrame = fresh(t.personAt, STALE.person) ? t.people : [];
      if (inFrame.length) {
        const names = inFrame.map(x => x.known?.name).filter((n): n is string => !!n);
        events.set(s => {
          const byName = { ...s.presence.byName };
          names.forEach(n => { byName[n] = now; });
          return { presence: { anyAt: now, byName } };
        });
        const key = names.slice().sort().join(', ');
        if (!present || key !== lastNames) {
          pushEvent(events, {
            source: 'person', level: 'info', kind: 'seen',
            text: names.length ? `👤 В кадре: ${names.join(', ')}` : `👤 Обнаружен человек${inFrame.length > 1 ? ` (${inFrame.length})` : ''}`,
          });
        }
        present = true;
        lastNames = key;
      } else if (present && now - events.get().presence.anyAt > PERSON_LOST_MS) {
        present = false;
        lastNames = '';
        pushEvent(events, { source: 'person', level: 'warn', kind: 'lost', text: '⚠️ Робот потерял человека' });
      }

      const m = fresh(t.missionAt, STALE.mission) ? t.mission : null;
      const newMode = m?.mode ?? null;
      if (newMode !== mode) {
        if (mode !== null || (newMode && newMode !== 'idle')) {
          pushEvent(events, {
            source: 'mission', level: 'info',
            text: newMode ? `Режим: ${MODE_NAMES[newMode] ?? newMode}` : 'Автономия не на связи',
          }, false);
        }
        mode = newMode;
      }
      if (m?.result && result !== undefined && m.result !== result && m.mode !== 'goto') {
        const ok = m.result === 'приехал';
        pushEvent(events, { source: 'mission', level: ok ? 'ok' : 'warn', text: ok ? '✅ Приехал к точке' : `⚠️ Поездка: ${m.result}` }, true);
      }
      // '' — миссия на связи, итога ещё нет; undefined — ещё не видели миссию (итог прошлой поездки не показываем)
      if (m) result = m.mode === 'goto' ? '' : m.result ?? '';
    };
    const t = setInterval(check, 1000);
    const unsub = telemetry.subscribe(() => {
      // Появление человека и смена режима — сразу, без ожидания таймера
      const s = telemetry.get();
      if ((!present && s.personAt > 0 && s.people.length) || (s.mission?.mode ?? null) !== mode) check();
    });
    return () => {
      clearInterval(t);
      unsub();
    };
  }, [events, telemetry]);
}
